/**
 * Balance anchors (PLAN §1). Every number in the content data is derived from or
 * sanity-checked against these. The harness compares sim output with `ROLE_RATIOS`.
 *
 * Absolute scale: a DPS player has ~40k HP and does ~4k single-target DPS, so the
 * HP : DPS ratio (~10) matches retail mid keys. Real NPC HP (Altar of Fangs, MDT)
 * is multiplied by `REAL_HP_SCALE` to get sim HP, which keeps the data's tier ratios.
 */
import type { NpcTier, Stats } from '@keystone/sim';

/** Sim HP per real-game HP point (25M boss → ~1.4M). */
export const REAL_HP_SCALE = 0.055;

/** Base HP per NPC tier (NpcDef.hpMult multiplies it). Real values: PLAN §1 HP tiers. */
/** First harness balance pass: all NPC HP × this (keeps tier ratios). */
export const NPC_HP_SCALE = 0.92;

export const TIER_HP: Record<NpcTier, number> = {
  fodder: Math.round(100_000 * NPC_HP_SCALE), // 1.0–2.0M
  standard: Math.round(180_000 * NPC_HP_SCALE), // 3.2M
  caster: Math.round(160_000 * NPC_HP_SCALE), // 2.9M
  big: Math.round(285_000 * NPC_HP_SCALE), // 5.2M
  miniboss: Math.round(500_000 * NPC_HP_SCALE), // 9.1M
  boss: Math.round(1_400_000 * NPC_HP_SCALE), // 20–30M
};

/** Player max HP by role. */
export const PLAYER_HP = { tank: 60_000, healer: 38_000, melee: 42_000, ranged: 38_000 } as const;

/** Target sustained single-target DPS of a pure DPS player (all other targets derive from it). */
export const DPS_TARGET = 4_000;

/** PLAN §1 role ratios (design anchors; observed ranges in comments). */
export const ROLE_RATIOS = {
  /** Tank damage taken ÷ one DPS's damage taken (3.0–5.3). */
  tankDamageTakenVsDps: 4,
  /** Tank self-healing ÷ tank damage taken (0.39–0.46). */
  tankSelfHealShare: 0.4,
  /** Healer HPS ÷ one DPS's DPS (0.26–0.43). */
  healerHpsVsDps: 0.35,
  /** Tank DPS ÷ DPS DPS (0.53–0.74). */
  tankDpsVsDps: 0.65,
  /** Healer DPS ÷ DPS DPS (0.13–0.23). */
  healerDpsVsDps: 0.2,
} as const;

/** Knobs for the harness balance pass: multiply every NPC melee hit / ability hit. */
export const NPC_MELEE_SCALE = 0.85;
export const NPC_ABILITY_SCALE = 1;
/** Extra multiplier on tank-buster damage (EnemyAbility.kind 'tankbuster'). */
export const NPC_BUSTER_SCALE = 0.75;
/** Extra multiplier on unavoidable area damage (to: 'area' pulses): group damage is mostly AoE (PLAN §1). */
export const NPC_AOE_SCALE = 2.1;
/** Extra multiplier on dodgeable telegraph / ground-zone damage. */
export const NPC_TELEGRAPH_SCALE = 1;

/** Shared base stats; classes override the defensive ones. */
export function baseStats(over: Partial<Stats> = {}): Stats {
  return {
    power: 1000,
    haste: 0,
    crit: 0.1,
    mastery: 0,
    versatility: 0,
    armor: 0.1,
    dodge: 0.03,
    parry: 0,
    block: 0,
    blockAmount: 0.3,
    missChance: 0,
    ...over,
  };
}

/** NPC melee hit size, scaled by the tuning knob. */
export const npcMelee = (dmg: number, speedMs = 2000) => ({ dmg: Math.round(dmg * NPC_MELEE_SCALE), speedMs });
/** NPC ability damage amount, scaled by the tuning knob. */
export const npcHit = (base: number) => ({ base: Math.round(base * NPC_ABILITY_SCALE), variance: 0.05 });
